import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { useCallback } from 'react';
import { useRealtimeScope } from '@/shared/hooks/useRealtimeScope';
import { useSupabaseRealtime } from '@/shared/hooks/useSupabaseRealtime';
import type { Tables } from '@/shared/types/database';
import {
  rowToAlbum,
  rowToComment,
  rowToPhoto,
  signPhotoRows,
} from '../services/supabaseAlbumService';
import { useAlbumStore } from '../stores/albumStore';

type AlbumRow = Tables<'albums'>;
type PhotoRow = Tables<'photos'>;
type PhotoCommentRow = Tables<'photo_comments'>;

/**
 * Realtime albums subscription (P7).
 *
 * Streams `public.albums`, `public.photos` and `public.photo_comments` changes
 * for the current family into the album store via the `upsertRealtime*` /
 * `deleteRealtime*` setters. A no-op when realtime is disabled (see
 * {@link useSupabaseRealtime}).
 *
 * Mirrors {@link module:features/todo-list/hooks/useRealtimeTasks} and
 * {@link module:features/family-calendar/hooks/useRealtimeEvents}.
 *
 * @module features/albums/hooks/useRealtimeAlbums
 */
export function useRealtimeAlbums(): void {
  const { familyId } = useRealtimeScope();

  const upsertRealtimeAlbum = useAlbumStore((s) => s.upsertRealtimeAlbum);
  const deleteRealtimeAlbum = useAlbumStore((s) => s.deleteRealtimeAlbum);
  const upsertRealtimePhoto = useAlbumStore((s) => s.upsertRealtimePhoto);
  const deleteRealtimePhoto = useAlbumStore((s) => s.deleteRealtimePhoto);
  const upsertRealtimeComment = useAlbumStore((s) => s.upsertRealtimeComment);
  const deleteRealtimeComment = useAlbumStore((s) => s.deleteRealtimeComment);
  const refetch = useAlbumStore((s) => s.refetch);

  const handleAlbumChange = useCallback(
    (payload: RealtimePostgresChangesPayload<AlbumRow>) => {
      if (payload.eventType === 'DELETE') {
        const oldId = payload.old.id;
        if (oldId) {
          deleteRealtimeAlbum(oldId);
        }
        return;
      }

      const row = payload.new;
      // Defense in depth: ignore rows outside the current family.
      if (familyId !== null && row.family_id !== familyId) {
        return;
      }

      // Derive photoCount/coverUrl from the photos already in the store; the
      // store preserves the locally-tracked count when the album already
      // exists, so a transient under-count on first sight self-corrects.
      //
      // No signing here, and none is needed: `cover.url` is a Photo the store
      // is already holding, so it is a signed URL from whichever fetch put it
      // there — never a raw key. When the cover photo is not in the store yet
      // there is simply no coverUrl and AlbumCard shows its own placeholder,
      // which is what it did before this wave too.
      const photos = useAlbumStore
        .getState()
        .photos.filter((p) => p.albumId === row.id);
      const cover = photos.find((p) => p.id === row.cover_photo_id);
      upsertRealtimeAlbum(rowToAlbum(row, photos.length, cover?.url));
    },
    [familyId, upsertRealtimeAlbum, deleteRealtimeAlbum]
  );

  const handlePhotoChange = useCallback(
    (payload: RealtimePostgresChangesPayload<PhotoRow>) => {
      if (payload.eventType === 'DELETE') {
        const oldId = payload.old.id;
        if (oldId) {
          deleteRealtimePhoto(oldId);
        }
        return;
      }

      const row = payload.new;
      if (familyId !== null && row.family_id !== familyId) {
        return;
      }

      // SIGNING INSIDE A SYNCHRONOUS CALLBACK.
      //
      // `photos.url` is a raw storage key since migration 020, and the bucket
      // is private, so a Photo is only renderable once its key has been signed
      // — which is a network round-trip. A postgres_changes handler cannot
      // await, so there are exactly two shapes available, and the choice is
      // between two different wrong-looking moments:
      //
      //   (a) upsert the unsigned row now, sign, upsert again. The row appears
      //       instantly, with `url === ''`, so a photo that arrives from
      //       another member's upload paints as the placeholder and then
      //       swaps to the image. Two store writes, and a visible flash on
      //       every single realtime photo event.
      //
      //   (b) sign first, upsert once — this. The row lands one round-trip
      //       late, complete. Nothing ever renders a placeholder for a photo
      //       that is fine, and the store is written once.
      //
      // (b) wins because the event is already asynchronous by nature: nobody
      // can tell the difference between "another device's upload showed up in
      // 80 ms" and "in 180 ms", but everybody sees a grey box that turns into a
      // photo. What is NOT an option is dropping the signing and upserting the
      // raw key into `url`: that renders as a relative-URL request against the
      // app's own origin and a broken image.
      //
      // `signPhotoRows` never rejects; a failed sign yields an empty lookup and
      // `rowToPhoto` falls back to `''`, so the row still reaches the store and
      // the caption/tags/cover-flag changes it carries are not lost.
      //
      // Known limit: two events for the same photo in flight together can land
      // out of order, since each waits on its own sign. Both carry the same
      // row shape and the loser is corrected by the next refetch, so this is
      // not worth a sequence number at this project's volume.
      void (async () => {
        const signed = await signPhotoRows([row]);
        upsertRealtimePhoto(rowToPhoto(row, signed));
      })();
    },
    [familyId, upsertRealtimePhoto, deleteRealtimePhoto]
  );

  const handleCommentChange = useCallback(
    (payload: RealtimePostgresChangesPayload<PhotoCommentRow>) => {
      if (payload.eventType === 'DELETE') {
        const oldId = payload.old.id;
        if (oldId) {
          deleteRealtimeComment(oldId);
        }
        return;
      }

      const row = payload.new;
      if (familyId !== null && row.family_id !== familyId) {
        return;
      }
      upsertRealtimeComment(rowToComment(row));
    },
    [familyId, upsertRealtimeComment, deleteRealtimeComment]
  );

  useSupabaseRealtime<AlbumRow>({
    table: 'albums',
    enabled: familyId !== null,
    ...(familyId !== null && { filter: `family_id=eq.${familyId}` }),
    onChange: handleAlbumChange,
    // Changes streamed while the channel was down are lost — refetch on
    // recovery to close the gap (one refetch covers all three tables).
    onReconnect: () => void refetch(),
  });

  useSupabaseRealtime<PhotoRow>({
    table: 'photos',
    enabled: familyId !== null,
    ...(familyId !== null && { filter: `family_id=eq.${familyId}` }),
    onChange: handlePhotoChange,
    onReconnect: () => void refetch(),
  });

  useSupabaseRealtime<PhotoCommentRow>({
    table: 'photo_comments',
    enabled: familyId !== null,
    ...(familyId !== null && { filter: `family_id=eq.${familyId}` }),
    onChange: handleCommentChange,
    onReconnect: () => void refetch(),
  });
}
